import { useCallback, useMemo, useSyncExternalStore } from 'react';

declare global {
    interface WindowEventMap {
        'session-storage': Event;
    }
}

// Dispatched by setValue so every hook on the same key re-reads, including the one that wrote.
// Session storage is per tab, so there is nowhere else a change could come from.
const changeEvent = 'session-storage';

const subscribe = (onStoreChange: () => void) => {
    window.addEventListener(changeEvent, onStoreChange);

    return () => window.removeEventListener(changeEvent, onStoreChange);
};

// Session storage does not exist on the server, so a server render only ever has the initial value
const getServerSnapshot = () => null;

/**
 * State kept in session storage, so it survives navigation within the tab but not a new one.
 * Values are stored as JSON, and anything unreadable falls back to `initialValue`.
 */
export function useSessionStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
    // The snapshot is the raw string rather than the parsed value: useSyncExternalStore compares
    // snapshots by identity, and parsing on every call would hand it a new object every time
    const getSnapshot = useCallback(() => {
        try {
            return window.sessionStorage.getItem(key);
        } catch (error) {
            console.warn(`Could not read sessionStorage key “${key}”:`, error);
            return null;
        }
    }, [key]);

    const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    const value = useMemo(() => {
        if (raw === null) {
            return initialValue;
        }

        try {
            return JSON.parse(raw) as T;
        } catch (error) {
            console.warn(`Could not parse sessionStorage key “${key}”:`, error);
            return initialValue;
        }
    }, [raw, key, initialValue]);

    const setValue = useCallback(
        (next: T) => {
            try {
                window.sessionStorage.setItem(key, JSON.stringify(next));
                window.dispatchEvent(new Event(changeEvent));
            } catch (error) {
                console.warn(`Could not write sessionStorage key “${key}”:`, error);
            }
        },
        [key],
    );

    return [value, setValue];
}
